import React, { useContext, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { Shield, TrendingUp, Activity, Lock, ArrowRight, Globe, ChevronRight } from 'lucide-react';
import CryptoMarquee from '../components/landing/CryptoMarquee';
import MarketChart from '../components/landing/MarketChart';
import solutionContent from '../data/solutionContent';
import '../styles/App.css';

const Landing = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const yHeroImage = useTransform(scrollYProgress, [0, 1], [0, 200]);
  
  const [visibleSolutions, setVisibleSolutions] = useState(8);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 80, damping: 20 } }
  };

  return (
    <div className="landing-page" style={{ 
      minHeight: '100vh', 
      backgroundColor: 'var(--bg-primary)', 
      color: 'var(--text-primary)',
      overflowX: 'hidden',
      position: 'relative'
    }}>
      
      {/* Background Mesh Gradients */}
      <div style={{
        position: 'absolute', top: '-10%', right: '-5%', width: '50vw', height: '50vw',
        background: 'radial-gradient(circle, var(--accent-primary) 0%, transparent 70%)',
        opacity: theme === 'dark' ? 0.08 : 0.03, filter: 'blur(100px)', zIndex: 0, pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', top: '40%', left: '-10%', width: '40vw', height: '40vw',
        background: 'radial-gradient(circle, var(--success) 0%, transparent 70%)',
        opacity: theme === 'dark' ? 0.05 : 0.02, filter: 'blur(120px)', zIndex: 0, pointerEvents: 'none'
      }} />

      {/* Hero Section (2-Column) */}
      <section className="hero-section" style={{
        padding: '8rem 5% 4rem', minHeight: '90vh', position: 'relative', zIndex: 1,
        display: 'flex', alignItems: 'center'
      }}>
        <div className="hero-container" style={{ 
          maxWidth: '1400px', margin: '0 auto', display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
          gap: '4rem', alignItems: 'center', width: '100%' 
        }}>
          
          {/* Left Text */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={itemVariants} style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(252, 213, 53, 0.1)',
              padding: '0.4rem 1rem', borderRadius: '30px', color: 'var(--accent-primary)',
              fontWeight: '600', fontSize: '0.85rem', marginBottom: '2rem', border: '1px solid rgba(252, 213, 53, 0.2)'
            }}>
              <Globe size={16} /> Welcome to the New Standard
            </motion.div>
            
            <motion.h1 variants={itemVariants} style={{
              fontSize: 'clamp(3.5rem, 5vw, 5.5rem)', fontWeight: '800', lineHeight: '1.05', marginBottom: '1.5rem',
              letterSpacing: '-0.03em'
            }}>
              Institutional Wealth,<br/>
              <span className="gradient-text">Engineered.</span>
            </motion.h1>
            
            <motion.p variants={itemVariants} className="hero-description" style={{
              fontSize: '1.25rem', color: 'var(--text-secondary)', maxWidth: '550px', marginBottom: '3rem',
              lineHeight: '1.6'
            }}>
              Quantis Capital merges BlackRock-grade algorithmic strategies with uncompromised digital asset security to generate unparalleled daily yields.
            </motion.p>
            
            <motion.div variants={itemVariants} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button 
                onClick={() => navigate('/register')}
                style={{
                  background: 'var(--accent-gradient)', padding: '1.1rem 2.5rem', borderRadius: '12px',
                  color: '#000', fontWeight: '800', fontSize: '1.1rem', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 8px 30px rgba(252, 213, 53, 0.3)',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Start Investing <ArrowRight size={20} />
              </button>
            </motion.div>

            {/* Quick Stats */}
            <motion.div variants={itemVariants} className="stats-row-hero" style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '4rem', borderTop: '1px solid var(--border-color)', paddingTop: '2rem' }}>
              <div>
                <h4 style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--text-primary)', margin: 0 }}>$2.4B+</h4>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.85rem' }}>Assets Managed</p>
              </div>
              <div>
                <h4 style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--text-primary)', margin: 0 }}>120k+</h4>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.85rem' }}>Active Investors</p>
              </div>
              <div>
                <h4 style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--text-primary)', margin: 0 }}>99.9%</h4>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.85rem' }}>Platform Uptime</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right 3D Graphic */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="hero-image-container"
            style={{ position: 'relative', y: yHeroImage, display: 'flex', justifyContent: 'center' }}
          >
            {/* Ambient glow behind image */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '80%', height: '80%', background: 'var(--accent-primary)',
              opacity: 0.15, filter: 'blur(80px)', borderRadius: '50%'
            }} />
            <motion.img 
              src={theme === 'dark' ? '/hero_crypto_3d.png' : '/hero_crypto_3d_light.png'} 
              alt="Crypto Wealth Management 3D" 
              className="hero-image"
              style={{ width: '100%', maxWidth: '600px', objectFit: 'contain', zIndex: 2, position: 'relative', filter: 'drop-shadow(0 30px 40px rgba(0,0,0,0.2))' }}
              animate={{ y: [0, -20, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </section>

      {/* Live Crypto Marquee */}
      <CryptoMarquee />

      {/* Trusted By / Social Proof Bar */}
      <section style={{ padding: '4rem 5%', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2rem' }}>Trusted by investors in 18+ countries</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '2rem', alignItems: 'center', justifyItems: 'center' }}>
            {[
              { val: '$2.4B+', label: 'AUM' },
              { val: '120,000+', label: 'Investors' },
              { val: '12', label: 'Asset Classes' },
              { val: '99.9%', label: 'Uptime' },
              { val: '78', label: 'NPS Score' }
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '0.2rem', color: 'var(--accent-primary)' }}>{s.val}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: 0 }}>{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section style={{ padding: '6rem 5%' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <Globe size={48} style={{ color: 'var(--accent-primary)', marginBottom: '1.5rem' }} />
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '800', marginBottom: '1rem', letterSpacing: '-0.02em' }}>Global Presence</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 3rem', fontSize: '1rem', lineHeight: '1.7' }}>
              Headquartered in New York with offices in London and Singapore, we provide 24-hour market coverage across every time zone.
            </p>
          </motion.div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              { city: 'New York', role: 'Global HQ', detail: 'Trading, Research, Compliance', img: '/office_ny.png' },
              { city: 'London', role: 'EMEA Hub', detail: 'FX, Commodities, Advisory', img: '/office_london.png' },
              { city: 'Singapore', role: 'APAC Hub', detail: 'Digital Assets, Asia Markets', img: '/office_singapore.png' }
            ].map((o, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                style={{ 
                  background: `linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.3) 100%), url(${o.img}) center/cover no-repeat`, 
                  padding: '2rem 1.5rem', 
                  borderRadius: '16px', 
                  border: '1px solid rgba(252, 213, 53, 0.2)',
                  minHeight: '220px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <h3 style={{ fontWeight: '800', marginBottom: '0.3rem', fontSize: '1.4rem', color: '#fff' }}>{o.city}</h3>
                <p style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '0.85rem', margin: '0 0 0.5rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{o.role}</p>
                <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.9rem' }}>{o.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" style={{ padding: '8rem 5%', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <h2 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>The Institutional Standard</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', fontSize: '1.1rem', lineHeight: '1.6' }}>
              Built for scale. Designed for performance. Secured by the most advanced cryptographic protocols in the industry.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem' }}>
            {[
              { icon: <TrendingUp size={36}/>, title: "Automated Yield Generation", desc: "Our proprietary algorithmic strategies execute thousands of micro-trades daily, ensuring consistent portfolio growth completely immune to emotional market volatility." },
              { icon: <Shield size={36}/>, title: "Bank-Grade Cold Storage", desc: "98% of all digital assets are held in geo-distributed, multi-signature cold wallets, strictly isolated from external internet access to prevent breaches." },
              { icon: <Activity size={36}/>, title: "Real-Time Transparency", desc: "Monitor your portfolio's performance with second-by-second analytics. We provide complete visibility into how and where your yields are generated." }
            ].map((feature, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                style={{
                  background: 'var(--bg-secondary)', padding: '3rem 2rem', borderRadius: '24px',
                  border: '1px solid var(--border-color)', boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
                  position: 'relative', overflow: 'hidden'
                }}
              >
                <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'var(--accent-primary)', opacity: 0.05, filter: 'blur(30px)', borderRadius: '50%' }} />
                
                <div style={{ 
                  background: 'rgba(252, 213, 53, 0.1)', width: '70px', height: '70px', borderRadius: '20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)',
                  marginBottom: '2rem', border: '1px solid rgba(252, 213, 53, 0.2)'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{ fontSize: '1.4rem', fontWeight: '800', marginBottom: '1rem', color: 'var(--text-primary)' }}>{feature.title}</h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', fontSize: '1.05rem' }}>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solutions Preview */}
      <section style={{ padding: '6rem 5%', position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', letterSpacing: '-0.02em' }}>Our Solutions</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', fontSize: '1.1rem' }}>
              Diversified exposure across the world's most liquid markets, powered by institutional-grade algorithms.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {Object.values(solutionContent).slice(0, visibleSolutions).map((sol, idx) => (
              <motion.div
                key={sol.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                onClick={() => navigate(`/solutions/${sol.id}`)}
                style={{
                  background: 'var(--bg-secondary)', padding: '2rem', borderRadius: '20px',
                  border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'all 0.3s',
                  position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column'
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{sol.emoji}</div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '0.5rem' }}>{sol.title}</h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '1.5rem', fontSize: '0.95rem', flexGrow: 1 }}>
                  {sol.desc.substring(0, 100)}...
                </p>
                <span style={{ color: 'var(--accent-primary)', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.9rem', marginTop: 'auto' }}>
                  Learn More <ArrowRight size={14} />
                </span>
              </motion.div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            {visibleSolutions < Object.keys(solutionContent).length && (
              <button onClick={() => setVisibleSolutions(prev => prev + 4)} style={{
                background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', padding: '0.8rem 2rem',
                borderRadius: '10px', color: 'var(--text-primary)', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--accent-primary)'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
              >Load More Solutions</button>
            )}
            <button onClick={() => navigate('/solutions')} style={{
              background: 'transparent', border: 'none', padding: '0.8rem 2rem',
              color: 'var(--text-secondary)', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', textDecoration: 'underline'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
            onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
            >Explore All 12 Asset Classes</button>
          </div>
        </div>
      </section>

      {/* Live Market Chart */}
      <MarketChart />

      {/* Institutional Allocation Vehicles */}
      <section id="plans" style={{ padding: '6rem 5% 10rem', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>Institutional Allocation Vehicles</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', fontSize: '1.1rem' }}>Sophisticated portfolio construction designed for capital preservation and uncorrelated alpha generation.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', alignItems: 'center' }}>
            {[
              { 
                name: 'Core Income Strategy', 
                profile: 'Capital preservation with steady quarterly distributions.',
                focus: 'Private Credit, Fixed Income, Real Assets',
                yield: '8% - 12%', 
                minimum: 50000, 
                cta: 'Request Prospectus' 
              },
              { 
                name: 'Alpha Multi-Strategy', 
                profile: 'Capital appreciation through algorithmic execution.',
                focus: 'Digital Assets, Global Macro, Forex Carry Trade',
                yield: '15% - 25%', 
                minimum: 250000, 
                cta: 'Apply for Allocation' 
              },
              { 
                name: 'Special Situations', 
                profile: 'Uncorrelated alpha via venture and distressed debt.',
                focus: 'Private Equity, Hedge Funds, Collectibles',
                yield: '25%+', 
                minimum: 1000000, 
                cta: 'Contact Advisory' 
              }
            ].map((plan, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.15 }}
                className="plan-card-premium"
                style={{
                  background: 'var(--bg-primary)', padding: '3rem 2.5rem', borderRadius: '24px',
                  border: idx === 1 ? '2px solid var(--accent-primary)' : '1px solid var(--border-color)', 
                  boxShadow: idx === 1 ? '0 20px 50px rgba(252, 213, 53, 0.15)' : '0 10px 30px rgba(0,0,0,0.05)',
                  transform: idx === 1 ? 'scale(1.05)' : 'scale(1)',
                  position: 'relative', overflow: 'hidden', cursor: 'pointer'
                }}
              >
                {idx === 1 && (
                  <div style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'var(--accent-gradient)', color: '#000', padding: '0.4rem 1rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '800' }}>
                    FLAGSHIP FUND
                  </div>
                )}
                
                <h3 style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>{plan.name}</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', minHeight: '48px' }}>{plan.profile}</p>
                
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '2rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '2rem' }}>
                  <span style={{ fontSize: '3rem', fontWeight: '800', color: 'var(--text-primary)', lineHeight: '1' }}>{plan.yield}</span>
                  <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>/ Target Net IRR</span>
                </div>

                <div style={{ marginBottom: '2.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                    <span style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Asset Focus</span>
                    <strong style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{plan.focus}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                    <span>Minimum Commitment:</span>
                    <strong style={{ color: 'var(--text-primary)' }}>${plan.minimum.toLocaleString()}</strong>
                  </div>
                </div>

                <button 
                  onClick={() => navigate('/register')}
                  style={{
                    width: '100%', padding: '1.2rem', borderRadius: '12px',
                    background: idx === 1 ? 'var(--accent-gradient)' : 'var(--bg-secondary)',
                    color: idx === 1 ? '#000' : 'var(--text-primary)', 
                    border: idx === 1 ? 'none' : '1px solid var(--border-color)',
                    fontWeight: '800', fontSize: '1.1rem', cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: idx === 1 ? '0 8px 25px rgba(252, 213, 53, 0.3)' : 'none'
                  }}
                  onMouseOver={(e) => { if(idx !== 1) e.currentTarget.style.borderColor = 'var(--accent-primary)'; }}
                  onMouseOut={(e) => { if(idx !== 1) e.currentTarget.style.borderColor = 'var(--border-color)'; }}
                >
                  {plan.cta}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding: '6rem 5%' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', letterSpacing: '-0.02em' }}>How It Works</h2>
            <p style={{ color: 'var(--text-secondary)', maxWidth: '500px', margin: '0 auto', fontSize: '1.05rem' }}>From account creation to portfolio performance in three simple steps.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              { step: '01', title: 'Create Your Account', desc: 'Complete our streamlined KYC process in under 5 minutes. Institutional-grade identity verification with bank-level encryption.' },
              { step: '02', title: 'Choose Your Strategy', desc: 'Select from 12 asset classes or let our advisory team build a bespoke multi-asset portfolio aligned to your goals.' },
              { step: '03', title: 'Watch Your Wealth Grow', desc: 'Our algorithms execute 24/7 while you monitor performance in real-time through your personal dashboard.' }
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                style={{ textAlign: 'center', padding: '2rem' }}
              >
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--accent-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '1.3rem', fontWeight: '900', color: '#000' }}>{s.step}</div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '0.8rem' }}>{s.title}</h3>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '0.95rem' }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '6rem 5%', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', letterSpacing: '-0.02em' }}>What Our Investors Say</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem' }}>Verified testimonials from institutional and private clients worldwide.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {[
              { quote: 'Quantis Capital gave our family office access to private equity co-investments we could never reach independently. The advisory team is world-class.', name: 'Richard S.', role: 'Family Office Principal, Switzerland', rating: 5 },
              { quote: 'The multi-asset platform is exceptional. I manage Forex, digital assets, and structured products from a single dashboard with institutional-grade execution.', name: 'Keiko M.', role: 'Portfolio Manager, Singapore', rating: 5 },
              { quote: 'Their carbon credit strategy generated 23% returns last year. The ESG impact reporting gives our board exactly the transparency they need.', name: 'Dr. Elizabeth W.', role: 'Endowment Director, United Kingdom', rating: 5 }
            ].map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.12 }}
                style={{ background: 'var(--bg-primary)', padding: '2rem', borderRadius: '20px', border: '1px solid var(--border-color)' }}
              >
                <div style={{ color: 'var(--accent-primary)', fontSize: '1.2rem', marginBottom: '1rem' }}>{'★'.repeat(t.rating)}</div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7', fontStyle: 'italic', marginBottom: '1.5rem', fontSize: '0.95rem' }}>"{t.quote}"</p>
                <div>
                  <p style={{ fontWeight: '700', margin: '0 0 0.2rem', fontSize: '0.95rem' }}>{t.name}</p>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.8rem' }}>{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="security" style={{ padding: '8rem 5%', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            maxWidth: '1000px', margin: '0 auto', background: 'var(--bg-modal)', padding: '5rem 2rem',
            borderRadius: '32px', border: '1px solid rgba(252, 213, 53, 0.3)', position: 'relative', zIndex: 1,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(20px)'
          }}
        >
          {/* Inner Glow */}
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '100%', height: '100%', background: 'var(--accent-primary)', opacity: 0.05, filter: 'blur(100px)', zIndex: -1 }} />
          
          <Lock size={64} style={{ color: 'var(--accent-primary)', margin: '0 auto 2rem' }} />
          <h2 style={{ fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', fontWeight: '800', marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>Unlock Your Portfolio's True Potential.</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.25rem', marginBottom: '3rem', maxWidth: '600px', margin: '0 auto 3rem' }}>
            Join thousands of elite investors maximizing their digital asset yields securely and transparently.
          </p>
          <button 
            onClick={() => navigate('/register')}
            style={{
              background: 'var(--text-primary)', padding: '1.2rem 3.5rem', borderRadius: '14px',
              color: 'var(--bg-primary)', fontWeight: '800', fontSize: '1.2rem', border: 'none', cursor: 'pointer',
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)', display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Create Free Account <ChevronRight size={20} />
          </button>
        </motion.div>
      </section>

    </div>
  );
};

export default Landing;
